// (B) HeadlessTable — antd Table 대체. @tanstack/react-table core(getCoreRowModel)로 행 모델을
// 생성하고, antd Table v5 DOM 을 `.om-table*`+`.om-table*` 이중클래스로 미러링한다.
//   .om-table-wrapper > [OmSpinner] > .om-table > .om-table-container > .om-table-content >
//     <table><colgroup><col/></colgroup><thead.om-table-thead><tbody.om-table-tbody> + .om-pagination
//   → 전역 officemail `.om-table*` SCSS(owma/etv) 무변경 매칭(JS 쿼리 0, rename 유예).
// ⚠ 정렬/필터는 서버구동(props.onChange)이라 헤더는 title 만 렌더(클라이언트 정렬/필터 모델 미사용).
// 셀은 컬럼 render(text, record, index) 결과를 그대로 출력(ngql 이 이미 .nine-* 래핑 완료).
import { useMemo, type Key, type ReactNode } from 'react'
import { useReactTable, getCoreRowModel, type ColumnDef } from '@tanstack/react-table'
import type { OmwTableColumn, OmwTableProps } from '../api/OmwTable'
import { OmwSpinner } from '../api/OmwSpinner'
import { OmwEmpty } from '../api/OmwEmpty'
import { OmwCheckbox } from '../api/OmwCheckbox'
import { OmwPagination } from '../api/OmwPagination'
import { cx } from './cx'
import '../styles/omwTable.css'

function getCellValue(record: unknown, dataIndex: OmwTableColumn<unknown>['dataIndex']): unknown {
  if (dataIndex == null || record == null) return undefined
  const rec = record as Record<string, unknown>
  if (Array.isArray(dataIndex)) {
    return dataIndex.reduce<unknown>(
      (acc, key) => (acc == null ? undefined : (acc as Record<string, unknown>)[key]),
      rec,
    )
  }
  return rec[dataIndex]
}

function alignStyle(
  align: OmwTableColumn<unknown>['align'],
): { textAlign: 'left' | 'center' | 'right' } | undefined {
  return align != null ? { textAlign: align } : undefined
}

export function HeadlessTable<T>({
  columns,
  dataSource,
  rowKey,
  loading = false,
  onRow,
  rowClassName,
  rowSelection,
  pagination,
  onChange,
  scroll,
  emptyText,
  className,
  style,
  'data-testid': dataTestid,
}: OmwTableProps<T>) {
  const getKey = useMemo(
    () =>
      typeof rowKey === 'function'
        ? rowKey
        : (record: T): Key => (record as Record<string, unknown>)[rowKey] as Key,
    [rowKey],
  )

  // @tanstack core 행 모델(데이터→행). 셀은 아래서 컬럼 render 로 직접 그린다(display column).
  const tanstackColumns = useMemo<ColumnDef<T>[]>(
    () => columns.map((col, index) => ({ id: col.key ?? `col-${index}` })),
    [columns],
  )
  const table = useReactTable<T>({
    data: dataSource,
    columns: tanstackColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(getKey(row)),
  })

  const rows = table.getRowModel().rows
  const isEmpty = rows.length === 0
  const hasSelection = rowSelection != null
  const selectedKeys = new Set<Key>(rowSelection?.selectedRowKeys ?? [])
  const totalColSpan = columns.length + (hasSelection ? 1 : 0)

  const toggleRow = (key: Key, checked: boolean) => {
    if (rowSelection?.onChange == null) return
    const next = new Set(selectedKeys)
    if (rowSelection.type === 'radio') {
      next.clear()
      if (checked) next.add(key)
    } else if (checked) {
      next.add(key)
    } else {
      next.delete(key)
    }
    const nextKeys = Array.from(next)
    const nextRows = dataSource.filter((r) => next.has(getKey(r)))
    rowSelection.onChange(nextKeys, nextRows)
  }

  const toggleAll = (checked: boolean) => {
    if (rowSelection?.onChange == null) return
    if (checked) {
      rowSelection.onChange(
        dataSource.map((r) => getKey(r)),
        [...dataSource],
      )
    } else {
      rowSelection.onChange([], [])
    }
  }

  const allChecked = !isEmpty && dataSource.every((r) => selectedKeys.has(getKey(r)))
  const someChecked = !allChecked && dataSource.some((r) => selectedKeys.has(getKey(r)))

  const contentStyle =
    scroll?.x != null ? { overflowX: 'auto' as const, overflowY: 'hidden' as const } : undefined
  const tableStyle =
    typeof scroll?.x === 'number'
      ? { minWidth: scroll.x, tableLayout: 'fixed' as const }
      : undefined

  const tableNode = (
    <div className={cx('om-table', isEmpty && 'om-table-empty')}>
      <div className="om-table-container">
        <div className="om-table-content" style={contentStyle}>
          <table style={tableStyle}>
            <colgroup>
              {hasSelection && <col style={{ width: 32 }} />}
              {columns.map((col, index) => (
                <col
                  key={col.key ?? `col-${index}`}
                  style={col.width != null ? { width: col.width } : undefined}
                />
              ))}
            </colgroup>
            <thead className="om-table-thead">
              <tr>
                {hasSelection && (
                  <th className="om-table-cell om-table-selection-column">
                    {rowSelection?.type !== 'radio' && (
                      <OmwCheckbox
                        checked={allChecked}
                        indeterminate={someChecked}
                        onChange={(checked) => toggleAll(checked)}
                      />
                    )}
                  </th>
                )}
                {columns.map((col, index) => (
                  <th
                    key={col.key ?? `col-${index}`}
                    className={cx('om-table-cell', col.className)}
                    style={alignStyle(col.align)}
                  >
                    {col.title as ReactNode}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="om-table-tbody">
              {isEmpty ? (
                <tr className="om-table-placeholder">
                  <td className="om-table-cell" colSpan={totalColSpan}>
                    <div className="om-table-expanded-row-fixed">{emptyText ?? <OmwEmpty />}</div>
                  </td>
                </tr>
              ) : (
                rows.map((row, rowIndex) => {
                  const record = row.original
                  const key = getKey(record)
                  const handlers = onRow?.(record, rowIndex)
                  const checkboxProps = rowSelection?.getCheckboxProps?.(record)
                  return (
                    <tr
                      key={String(key)}
                      data-row-key={String(key)}
                      className={cx('om-table-row', rowClassName?.(record, rowIndex))}
                      onClick={handlers?.onClick}
                      onMouseEnter={handlers?.onMouseEnter}
                      onMouseLeave={handlers?.onMouseLeave}
                      onMouseDown={handlers?.onMouseDown}
                    >
                      {hasSelection && (
                        <td className="om-table-cell om-table-selection-column">
                          <OmwCheckbox
                            checked={selectedKeys.has(key)}
                            disabled={checkboxProps?.disabled}
                            onChange={(checked) => toggleRow(key, checked)}
                          />
                        </td>
                      )}
                      {columns.map((col, colIndex) => {
                        const value = getCellValue(record, col.dataIndex)
                        const content =
                          col.render != null
                            ? col.render(value, record, rowIndex)
                            : (value as ReactNode)
                        return (
                          <td
                            key={col.key ?? `col-${colIndex}`}
                            className={cx('om-table-cell', col.className)}
                            style={alignStyle(col.align)}
                          >
                            {content}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  // pagination: false 아니고 hideOnSinglePage(단일 페이지 숨김) 아닐 때 노출.
  let paginationNode: ReactNode = null
  if (pagination !== false && pagination != null) {
    const { current, pageSize = 10, total = 0, hideOnSinglePage } = pagination
    const singlePage = total <= pageSize
    if (!(hideOnSinglePage === true && singlePage)) {
      paginationNode = (
        <OmwPagination
          className="om-table-pagination"
          current={current}
          pageSize={pageSize}
          total={total}
          showSizeChanger={pagination.showSizeChanger}
          pageSizeOptions={pagination.pageSizeOptions}
          onChange={(page, size) => {
            // 서버구동: props.onChange(테이블 레벨) 우선, 없으면 pagination.onChange(movePage).
            if (onChange != null) {
              onChange({ ...pagination, current: page, pageSize: size }, {}, undefined, {
                action: 'paginate',
              })
            } else {
              pagination.onChange?.(page, size)
            }
          }}
        />
      )
    }
  }

  return (
    <div className={cx('om-table-wrapper', className)} style={style} data-testid={dataTestid}>
      <OmwSpinner spinning={loading}>{tableNode}</OmwSpinner>
      {paginationNode}
    </div>
  )
}
